import { memo } from 'preact/compat';

// Components.
import { Country, SingleCountry } from './Country';

// Country settings type.
export type CountrySetting = {
	data: { [ key: string ]: any };
	pathAttributes: { [ key: string ]: string };
};

export type CountriesProps = {
	countries: Array<SingleCountry>;
	countrySettings: { [ key: number ]: CountrySetting };
	onCountryClick: Function;
	countryDefaultAttributes: { [ key: string ]: string };
};

/**
 * Countries component.
 * Render all countries. Combine settings by comparing country id.
 * Memo'd to reduce re-renders.
 *
 * @param {Object}   props
 * @param {Array}    props.countries                - array of countries with path data
 * @param {Object}   props.countrySettings          - object of country settings. Matched against country IDs to add data and apply attributes as needed including fill, classNames, etc.
 * @param {Function} props.onCountryClick           - country click callback function. This function will be called with the country object and data object.
 * @param {Object}   props.countryDefaultAttributes - default attributes to apply to all countries such as fill, stroke, className, etc.
 * @return {*} jsx
 */
export const Countries = memo( ( { countries, countrySettings, onCountryClick, countryDefaultAttributes }: CountriesProps ) => {
	return (
		<>
			{ countries.map( ( country ) => {
				if ( ! country || ! country.id || ! country.d ) {
					return null;
				}

				const { id } = country;

				// Use settings for this country if available.
				const settings = countrySettings?.[ id ] || { data: {}, pathAttributes: {} };

				const { data } = settings;

				// Combine default attributes with country settings.
				const attributes = {
					...countryDefaultAttributes,
					...settings.pathAttributes,
				};

				return (
					<Country
						key={ `country-${ id }` }
						country={ country }
						data={ data }
						pathAttributes={ attributes }
						callback={ () => onCountryClick( { country, data } ) }
					/>
				);
			} ) }
		</>
	);
} );
